import {Component, Input, OnInit, ViewChild} from "@angular/core";
import {Subject} from "rxjs/internal/Subject";
import {Observable} from "rxjs/internal/Observable";
import {AddressSearchService} from "./address-search.service";
import {Place} from "./place";
import {finalize, switchMap} from "rxjs/operators";
import {FormGroup} from "@angular/forms";
import {dropDowns} from "../../shared/drop-downs";
import {PostalCodeExample, PostalCodePatern} from "../../shared/constants";
import {MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";

export const NO_RESULTS_FOUND = 'No records available';

@Component({
    selector    : 'dp-address-form',
    templateUrl : './address-form.component.html',
})
export class AddressFormComponent implements OnInit {

    @Input() formGroup: FormGroup;
    @Input() formArrayName: string;
    @Input() formGroupName: string;
    @Input() formArrayIndex: number;
    @Input() addressLine1Label: string = 'Address 1';
    @Input('addressLine1MaxLength') addressLine1MaxLength : number = 256;
    @Input('addressLine2MaxLength') addressLine2MaxLength : number = 256;
    @Input('cityMaxLength') cityMaxLength : number = 100;

    @ViewChild('provinceSelect') provinceSelect: MatSelect;
    @ViewChild('countrySelect') countrySelect: MatSelect;

    @Input('prefixForVisibilityKey') prefixForVisibilityKey: string;
    addressLine1VisibilityKey: string;
    addressLine2VisibilityKey: string;
    cityVisibilityKey: string;
    countryVisibilityKey: string;
    postalCodeVisibilityKey: string;
    provinceCodeVisibilityKey: string;

    searchAddressStream = new Subject<string>();
    addressSuggestions : any[] = [];
    addressSuggestionsOptions : string[] = [];
    provinceOptions : any = dropDowns.provinceOptions;
    countryOptions: any = dropDowns.countryList;

    constructor(private addressSearchService: AddressSearchService) {  }

    ngOnInit() {
        this.initializeVisibilityKeys();
        // AutoComplete Address
        this.searchAddressStream
            .pipe(switchMap((term : string) => {
                    if(term.trim() == '') {
                        let observable = Observable.create((observer) => {
                            setTimeout(() => {
                                observer.next();
                            }, 10);
                        });
                        return observable;
                    } else {
                        return this.addressSearchService.searchPlaces(term, this.country);
                    }
            }))
            .subscribe(
                (data : Place[]) => {
                    console.log(data);

                    this.addressSuggestions = data;
                },
                error => {

                }
            );
    }

    searchAddresses(event) : void {
        let entered : string = event.target.value;
        if(entered && entered.trim().length > 0) {
            this.searchAddressStream.next(entered);
        }
    }

    dataSelectedAddress(event, trigger: MatAutocompleteTrigger) {
        this.addressSuggestions = [];
        this.addressLine1 = '';
        if(event && event.description && event.description.indexOf(NO_RESULTS_FOUND) > -1) {
            this.resetAddressFields();
        } else {
            if(event.source == 'CANADA_POST' && event.nextStep == 'Find') {
                this.getPlaceListById(event , trigger);
            } else {
                this.getPlaceDetail(event);
            }
        }
    }

    getPlaceListById(selectedPlace : Place, trigger: MatAutocompleteTrigger) : void {
        let encodedPlaceId : string = encodeURIComponent(selectedPlace.placeId);
        this.addressSearchService.searchPlacesById(encodedPlaceId).subscribe(
            (data : Place[]) => {
                this.addressSuggestions = data;
                if(data.length == 0) {
                    this.resetAddressFields();
                } else {
                    trigger.openPanel();
                }
            }
        );
    }

    getPlaceDetail(selectedPlace : Place) : void {
        this.addressSearchService.getPlaceDetail(selectedPlace).subscribe(
            (place : Place) => {
                if(place) {
                    this.addressLine1 = place.addressLine1;
                    this.addressLine2 = place.addressLine2;
                    this.city = place.city;
                    this.country = place.country == 'CANADA' ? 'CANADA' : 'USA';
                    this.provinceCode = place.provinceCode;
                    this.postalCode = place.postalCode;

                } else {
                    this.resetAddressFields();
                }
            }
        );
    }

    resetAddressFields() : void {
        this.addressLine1 = '';
        this.addressLine2 = '';
        this.city = '';
        this.provinceCode = '';
        this.postalCode = '';
        this.country = 'CANADA';
    }

    get formControls()  {

        if(this.formArrayName){
            return this.formGroup?.get(this.formArrayName)['controls'][this.formArrayIndex]?.controls;
        } else {
            return this.formGroup?.get(this.formGroupName)['controls'];
        }
    }

    isCountryCanada() : boolean {
        return this.country == 'CANADA';

    }

    get postlCodePatern() : string {
        return this.isCountryCanada() ? PostalCodePatern.Canada : PostalCodePatern.USA;
    }

    get postalCodeExample() : string {
        return this.isCountryCanada() ? PostalCodeExample.Canada : PostalCodeExample.USA;
    }

    get addressLine1() : string {
        return this.formControls.addressLine1?.value;
    }
    set addressLine1(value: string) {
        this.formControls.addressLine1.setValue(value);
    }

    get addressLine2() : string {
        return this.formControls.addressLine2?.value;
    }
    set addressLine2(value: string) {
        this.formControls.addressLine2.setValue(value);
    }

    get city() : string {
        return this.formControls.city?.value;
    }
    set city(value: string) {
        this.formControls.city.setValue(value);
    }

    get provinceCode() : string {
        return this.formControls.provinceCode?.value;
    }
    set provinceCode(value: string) {
        this.formControls.provinceCode.setValue(value);
    }

    get postalCode() : string {
        return this.formControls.postalCode?.value;
    }
    set postalCode(value: string) {
        this.formControls.postalCode.setValue(value);
    }

    get country() : string {
        return this.formControls.country?.value;
    }
    set country(value: string) {
        this.formControls.country.setValue(value);
    }

    private initializeVisibilityKeys() {
        this.addressLine1VisibilityKey = this.prefixForVisibilityKey  + '.addressLine1';
        this.addressLine2VisibilityKey = this.prefixForVisibilityKey  + '.addressLine2';
        this.cityVisibilityKey = this.prefixForVisibilityKey  + '.city';
        this.countryVisibilityKey = this.prefixForVisibilityKey  + '.country';
        this.postalCodeVisibilityKey = this.prefixForVisibilityKey  + '.postalCode';
        this.provinceCodeVisibilityKey = this.prefixForVisibilityKey  + '.provinceCode';
    }
}
